import type { GridMatch } from '@/types/tournament.model';
import { cellKey, type CellModel, type GridModel } from './grid-model';

/**
 * The order that decides court numbers inside a cell.
 *
 * Courts are **derived, not stored**: there is no court in the data model. `MatchModel.court` is
 * a dead FK and `courtReference` is free text nobody writes; a block only declares how many
 * courts exist (`courtsQuantity`). So "Cancha 2" means "the second match of this cell in this
 * order" and nothing more.
 *
 * The order therefore has to be stable, or the same day would print with different court numbers
 * on two consecutive exports. Tournament position in the grid first (so a tournament's matches
 * cluster together instead of interleaving), `matchId` as the tie-break (arbitrary but total and
 * unchanging).
 *
 * What this deliberately does NOT promise: stability across a *move*. Taking a match out of a
 * cell shifts the ones after it up a court. That is the accepted cost of not persisting the
 * assignment, and the reason the PDF button is disabled while there are unsaved changes.
 *
 * Copies before sorting: `cell.matches` belongs to the model, and sorting it in place would make
 * the weekly view's card order depend on whether the day view happened to render first.
 */
export function sortForCourts(
  matches: GridMatch[],
  tournamentOrder: Map<string, number>,
): GridMatch[] {
  return [...matches].sort((a, b) => {
    const orderA = tournamentOrder.get(a.tournamentId) ?? Number.MAX_SAFE_INTEGER;
    const orderB = tournamentOrder.get(b.tournamentId) ?? Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) return orderA - orderB;
    // Plain code-unit comparison, not `localeCompare`: the whole promise of this function is that
    // the same matches yield the same court numbers everywhere, and locale-aware collation varies
    // with the viewer's browser and OS settings.
    return a.matchId < b.matchId ? -1 : a.matchId > b.matchId ? 1 : 0;
  });
}

export type DayCourtCell = {
  match: GridMatch | null;
  /**
   * Past the cell's declared capacity, and only on a column that actually holds a match: an
   * empty padding column is never marked as overflow.
   */
  overflow: boolean;
};

export type DayRowModel = {
  time: string;
  cell: CellModel;
  /** Always `courtCount` long, padded with `match: null`. */
  courts: DayCourtCell[];
};

export type DayModel = {
  date: string;
  courtCount: number;
  rows: DayRowModel[];
};

/**
 * One day of the grid laid out as courts × time slots.
 *
 * Rows are every time on the weekly model's axis, including the ones this day has nothing on, so
 * the two views line up row for row and switching between them doesn't move things around.
 */
export function buildDay(
  model: GridModel,
  date: string,
  tournamentOrder: Map<string, number>,
): DayModel {
  const cells = model.times
    .map((time) => model.cells.get(cellKey(date, time)))
    .filter((cell): cell is CellModel => cell !== undefined);

  // Wide enough for the declared capacity AND for every match actually placed, so an overbooked
  // slot never pushes a match past the edge of the table where nobody would see it. Floor of 1
  // keeps the header from collapsing on a day with no capacity and no matches.
  const courtCount = Math.max(
    1,
    ...cells.map((cell) => Math.max(cell.courtsQuantity ?? 0, cell.matches.length)),
  );

  const rows = cells.map((cell) => {
    const sorted = sortForCourts(cell.matches, tournamentOrder);
    const courts: DayCourtCell[] = Array.from({ length: courtCount }, (_, i) => ({
      match: sorted[i] ?? null,
      overflow:
        sorted[i] !== undefined && cell.courtsQuantity !== null && i >= cell.courtsQuantity,
    }));
    return { time: cell.startTime, cell, courts };
  });

  return { date, courtCount, rows };
}
